import * as React from 'react';

import { useTranslations } from '@rematter/modules/trade/i18n/useTranslations';
import { cn, MaterialSymbol } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideCog } from 'lucide-react';

import {
  $materialSortingRules,
  $openMaterialsModal,
} from '../../../../_state/loadStaged.state';
import { isCatalyticConverter } from '../../../../utils/catalyticConverter';
import { LoadStagedFieldProps } from '../_types';

import { useInventoryGroupStyle } from '~/modules/trade/hooks/useInventoryGroupStyle';
import { TouchSelectOption } from '~/shared/components/select-fields/helpers/createEntityTouchSelect';
import { SelectMaterialTouch } from '~/shared/components/select-fields/SelectMaterialTouch';

export const InventoryItemSelectField: React.FC<LoadStagedFieldProps> = ({
  lineItem,
  inventoryItem,
  className,
  disabled,
  touchModeEnabled,
  onUpdateItem,
  onFocus,
  onBlur,
}) => {
  const t = useTranslations();
  const sortingRules = useAtomValue($materialSortingRules);
  const openMaterialsModal = useSetAtom($openMaterialsModal);

  const groupStyle = useInventoryGroupStyle({});

  const handleItemChange = React.useCallback(
    (itemId?: string) => {
      onUpdateItem(lineItem.id, { itemId });
    },
    [lineItem.id, onUpdateItem],
  );

  const handleClickTouchMode = React.useCallback(() => {
    openMaterialsModal({
      mode: 'change',
      itemType: 'items',
      selectedItemId: lineItem.itemId ?? null,
      lineItemId: lineItem.id,
    });
  }, [openMaterialsModal, lineItem.itemId, lineItem.id]);

  const sortOptions = React.useCallback(
    (options: TouchSelectOption[]) => {
      const { salesOrderItemIds, requestedItemIds } = sortingRules;

      if (salesOrderItemIds.size === 0 && requestedItemIds.size === 0) {
        return options;
      }

      return [...options].sort((a, b) => {
        const getPriority = (option: TouchSelectOption) => {
          const isInSalesOrder = salesOrderItemIds.has(option.id);
          const hasRequestedWeight = requestedItemIds.has(option.id);

          if (isInSalesOrder && hasRequestedWeight) return 1;
          if (hasRequestedWeight && !isInSalesOrder) return 2;
          return 3;
        };

        return getPriority(a) - getPriority(b);
      });
    },
    [sortingRules],
  );

  const materialSymbol = (
    <MaterialSymbol
      description={inventoryItem?.mnemonic ?? undefined}
      style={groupStyle}
      className="h-[44px] w-[35px] max-w-[35px]"
      symbolIcon={
        isCatalyticConverter(inventoryItem?.category?.name) ? (
          <LucideCog size={16} />
        ) : null
      }
    />
  );

  return (
    <div
      className={cn(
        '@InventoryItemSelectField flex items-center gap-2',
        className,
      )}
    >
      {materialSymbol}
      <SelectMaterialTouch
        value={lineItem.itemId ?? undefined}
        onChange={handleItemChange}
        placeholder={t.loadStaged.placeholders.material}
        onFocus={onFocus}
        onBlur={onBlur}
        isDisabled={disabled}
        variant="outlined"
        sortOptions={sortOptions}
        onTriggerClick={touchModeEnabled ? handleClickTouchMode : undefined}
      />
    </div>
  );
};
